import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { EmptyState, ProductCard, Skeleton, getDiscountPercent } from "@aurelia/ui";
import { useCategories, useProducts } from "../../features/catalog/queries";
import { useWishlist, useToggleWishlist } from "../../features/wishlist/queries";
import { useAddToCart } from "../../features/cart/mutations";
import { useAuthStore } from "../../lib/stores/auth";
import { HeroSceneLazyMount } from "../../components/three/HeroSceneLazyMount";
import { RevealOnScroll } from "../../components/motion/RevealOnScroll";
import { IntroSplash } from "../../components/motion/IntroSplash";
import { CategoryTile } from "../../components/home/CategoryTile";
import { CategoryQuickLinks } from "../../components/home/CategoryQuickLinks";
import { HomeStatsRow } from "../../components/home/HomeStatsRow";
import { HeroShowcase } from "../../components/home/HeroShowcase";
import { TrustSignals } from "../../components/home/TrustSignals";
import { PromoBanner } from "../../components/home/PromoBanner";
import { useHeroOverlapStore } from "../../lib/stores/heroOverlap";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { useStoreConfig } from "../../features/config/queries";
import { toast } from "@aurelia/ui";
import { PRIMARY_KEYWORDS } from "../../lib/seoConstants";

const HEADER_HEIGHT = 64;

export function HomePage() {
  useDocumentMeta(
    "Latif General Store – Wholesale & Retail Store in Pakistan",
    "Shop toys, garments, hosiery and undergarments online from Latif General Store — wholesale and retail across Pakistan with fast delivery.",
    PRIMARY_KEYWORDS
  );
  const { data: categories, isLoading: categoriesLoading, isError: categoriesError } = useCategories();
  const { data: bestSellers } = useProducts({ onlyBestSellers: true, pageSize: 8, includeRatings: true });
  const {
    data: newArrivals,
    isLoading: productsLoading,
    isError: productsError
  } = useProducts({ onlyNewArrivals: true });
  const { data: config } = useStoreConfig();
  const { data: wishlist } = useWishlist();
  const toggleWishlist = useToggleWishlist();
  const addToCart = useAddToCart();
  const isAuthenticated = useAuthStore((s) => s.status === "authenticated");
  const wishlistedIds = new Set(wishlist?.map((p) => p.id));
  const heroRef = useRef<HTMLElement>(null);
  const setOverDarkHero = useHeroOverlapStore((s) => s.setOverDarkHero);

  const combinedProducts = [...(bestSellers?.items ?? []), ...(newArrivals?.items ?? [])];

  // Real, derived from actual catalog pricing — never hardcoded, and only
  // shown when a genuine discount exists somewhere in these two sets.
  const maxDiscountPercent = combinedProducts.reduce<number | null>((max, p) => {
    const pct = getDiscountPercent(p.basePrice, p.compareAtPrice);
    return pct !== null && (max === null || pct > max) ? pct : max;
  }, null);

  // A category without its own admin-uploaded image borrows a photo from
  // one of its own real products in the quick-links row, rather than
  // showing an empty placeholder — still real catalog data either way.
  const categoryImageFallback = combinedProducts.reduce<Record<string, string>>((map, p) => {
    const slug = p.category?.slug;
    const image = p.images[0];
    if (slug && image && !map[slug]) map[slug] = image;
    return map;
  }, {});

  const handleQuickAdd = (variantId: string | undefined) => {
    if (!isAuthenticated) {
      toast("Sign in to add items to your cart", "danger");
      return;
    }
    if (!variantId) return;
    addToCart.mutate({ variantId, quantity: 1 });
  };

  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl) return;
    const updateOverlap = () => {
      setOverDarkHero(heroEl.getBoundingClientRect().bottom > HEADER_HEIGHT);
    };
    updateOverlap();
    window.addEventListener("scroll", updateOverlap, { passive: true });
    window.addEventListener("resize", updateOverlap);
    return () => {
      window.removeEventListener("scroll", updateOverlap);
      window.removeEventListener("resize", updateOverlap);
      setOverDarkHero(false);
    };
  }, [setOverDarkHero]);

  return (
    <main>
      <IntroSplash />
      <div className="dark bg-[#050813]">
      <section
        ref={heroRef}
        className="relative overflow-hidden px-6 py-10 sm:py-14 lg:py-12"
      >
        <HeroSceneLazyMount />

        <div className="relative mx-auto max-w-7xl lg:grid lg:grid-cols-2 lg:items-center lg:gap-12">
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left gap-3 sm:gap-5">
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.04] px-3.5 py-1.5 text-xs font-medium text-accent tracking-[0.15em] uppercase"
            >
              <Sparkles size={12} aria-hidden />
              New collection
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="text-display-md max-w-md text-ink text-balance"
            >
              Hair accessories &amp; everyday essentials, <span className="text-accent">made with elegance.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="text-ink-muted max-w-md text-base sm:text-lg"
            >
              Toys, garments, and hair accessories — hair bands, catchers, and clips — designed with restraint,
              priced with respect.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4"
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/category/catchers"
                  className="inline-flex items-center justify-center gap-2.5 h-14 sm:h-16 pl-8 sm:pl-10 pr-2.5 sm:pr-3 rounded-full bg-accent text-ink text-base sm:text-lg font-medium tracking-tight shadow-glow-accent hover:bg-accent-strong transition-colors duration-300 ease-physical active:scale-95"
                >
                  Shop Collection
                  <span className="h-9 w-9 sm:h-11 sm:w-11 rounded-full bg-ink/90 text-accent flex items-center justify-center shrink-0">
                    <ArrowRight size={16} />
                  </span>
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <a
                  href="#categories"
                  className="inline-flex items-center justify-center h-14 sm:h-16 px-6 sm:px-8 rounded-full border border-white/20 text-ink text-base sm:text-lg font-medium tracking-tight hover:border-white/40 hover:bg-white/5 transition-colors duration-300 ease-physical active:scale-95"
                >
                  Browse Categories
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-3 gap-2.5 sm:gap-3 w-full max-w-md pt-1"
            >
              {[
                { icon: Truck, title: "Fast delivery", detail: "Across Pakistan" },
                { icon: ShieldCheck, title: "Quality products", detail: "Carefully selected" },
                { icon: RotateCcw, title: "Easy returns", detail: "Hassle free" }
              ].map(({ icon: Icon, title, detail }) => (
                <div
                  key={title}
                  className="flex flex-col items-center lg:items-start gap-1 rounded-xl border border-white/10 bg-white/[0.025] px-2.5 py-2.5 text-center lg:text-left"
                >
                  <Icon size={14} className="text-accent shrink-0" aria-hidden />
                  <span className="text-xs font-medium text-ink leading-tight">{title}</span>
                  <span className="text-[11px] text-ink-muted leading-tight">{detail}</span>
                </div>
              ))}
            </motion.div>
          </div>

          <HeroShowcase products={bestSellers?.items ?? []} />
        </div>
      </section>

      {categories && categories.length > 0 && (
        <CategoryQuickLinks
          categories={categories}
          maxDiscountPercent={maxDiscountPercent}
          categoryImageFallback={categoryImageFallback}
          footer={config?.stats ? <HomeStatsRow stats={config.stats} /> : undefined}
        />
      )}
      </div>

      {!!bestSellers?.items.length && (
        <section className="max-w-7xl mx-auto px-6 pt-12 sm:pt-16 pb-20">
          <RevealOnScroll>
            <h2 className="text-display-sm mb-8">Best sellers</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {bestSellers.items.map((p, i) => (
              <RevealOnScroll key={p.id} delay={(i % 4) * 0.05}>
                <ProductCard
                  slug={p.slug}
                  name={p.name}
                  shortDescription={p.shortDescription}
                  imageUrl={p.images[0] ?? null}
                  basePrice={p.basePrice}
                  compareAtPrice={p.compareAtPrice}
                  soldCount={p.soldCount}
                  stock={p.variants.reduce((sum, v) => sum + v.stock, 0)}
                  wishlisted={wishlistedIds.has(p.id)}
                  onToggleWishlist={() => toggleWishlist.mutate(p.id)}
                  rating={
                    p.reviewCount ? { average: p.avgRating ?? 0, count: p.reviewCount } : undefined
                  }
                  onQuickAdd={() => handleQuickAdd(p.variants[0]?.id)}
                />
              </RevealOnScroll>
            ))}
          </div>
        </section>
      )}

      {config?.banner && (
        <RevealOnScroll className="block">
          <PromoBanner imageUrl={config.banner.imageUrl} text={config.banner.text} link={config.banner.link} />
        </RevealOnScroll>
      )}

      <section id="categories" className="max-w-7xl mx-auto px-6 py-20 scroll-mt-20">
        <RevealOnScroll>
          <h2 className="text-display-sm mb-8">Shop by category</h2>
        </RevealOnScroll>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {categoriesLoading &&
            Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="aspect-square" />)}
          {categoriesError && (
            <EmptyState tone="error" title="Categories unavailable" description="Couldn't reach the store right now — please refresh." />
          )}
          {categories?.map((cat, i) => (
            <RevealOnScroll key={cat.id} delay={i * 0.05}>
              <CategoryTile category={cat} index={i} />
            </RevealOnScroll>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <RevealOnScroll>
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-display-sm">New arrivals</h2>
          </div>
        </RevealOnScroll>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {productsLoading &&
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-square" />)}
          {newArrivals?.items.map((p, i) => (
            <RevealOnScroll key={p.id} delay={(i % 4) * 0.05}>
              <ProductCard
                slug={p.slug}
                name={p.name}
                shortDescription={p.shortDescription}
                imageUrl={p.images[0] ?? null}
                basePrice={p.basePrice}
                compareAtPrice={p.compareAtPrice}
                soldCount={p.soldCount}
                stock={p.variants.reduce((sum, v) => sum + v.stock, 0)}
                wishlisted={wishlistedIds.has(p.id)}
                onToggleWishlist={() => toggleWishlist.mutate(p.id)}
              />
            </RevealOnScroll>
          ))}
          {productsError && (
            <EmptyState tone="error" title="Products unavailable" description="Couldn't reach the store right now — please refresh." />
          )}
          {!productsLoading && !productsError && newArrivals?.items.length === 0 && (
            <EmptyState title="No products yet" description="Check back soon." />
          )}
        </div>
      </section>

      <RevealOnScroll className="block">
        <TrustSignals />
      </RevealOnScroll>

      <RevealOnScroll className="block">
        <section className="max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="text-display-md mb-4">Built on restraint, not trend.</h2>
          <p className="text-ink-muted text-lg">
            Every product we carry is chosen for how it holds up — not just how it photographs. That's the whole
            idea behind Latif General Store.
          </p>
        </section>
      </RevealOnScroll>
    </main>
  );
}
